import React, { memo, useState } from "react";
import clsx from "clsx";
import styles from "./Wheels.module.scss";
import Text from "../../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../../../shared-components/css-modules/lexus/atoms/Button";
import type { AtmosphereWinterWheelsType } from "../../../../../../../utils/types/AtmosphereType";
import { scrollToTarget } from "../../../../../../../../../../common-deprecated/utils/scrollUtils";
import { CarConfigConfigureSectionsLbx } from "../../../../../../../utils/types/SectionTypesLbx";
import { trackViewAtmosphereWheels, trackWheelsTyreMoreInfoOpen } from "../../../../../../../utils/tracking";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../../utils/labelUtils";
import useAphLbxWinterWheel from "../../../../../../../hooks/lbx/useAphLbxWinterWheel";
import WheelEcoLabel from "../../../../../configure/sections/wheels/WheelEcoLabel";
import Price from "../../../../../common/price/Price";
import { showAccessoriesRate } from "../../../../../../../utils/financeInsuranceRate";
import { useCarConfigSelector } from "../../../../../../../redux/store";
import SidePanel from "../../../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import WheelDetails from "../../../../../configure/sections/wheels/WheelDetails";
import { getCarDBAssetSrcSet } from "../../../../../../../../../shared-logic/utils/imageUtils";
import Icon from "../../../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import type { EuLabelIcons } from "../../../../../../../utils/types/SectionTypes";

export type Props = {
    index: number;
    wheel: AtmosphereWinterWheelsType;
    categoryName: string;
};

const WinterWheel: React.FC<Props> = (props): JSX.Element => {
    const { index, wheel, categoryName } = props;
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const [showInfo, setShowInfo] = useState<boolean>(false);
    const sidePanelTitle = useCarConfigLabel("carConfigTyreInfo");

    const { onClick, isSelectable, isActive } = useAphLbxWinterWheel(wheel, categoryName);
    return (
        <>
            <article
                className={clsx(styles.wheel, isActive && styles.active)}
                style={{
                    "--or-animation-delay": `${(index + 1) * 100 + 4}`,
                }}
            >
                <Text tag="h4" variant="apheleia-h5">
                    {wheel.name}
                </Text>

                {wheel.euLabelProperties && (
                    <div className={styles.labels}>
                        {Object.entries(wheel.euLabelProperties).map(([key, value]) => (
                            <WheelEcoLabel type={key as EuLabelIcons} score={value} key={key} />
                        ))}
                    </div>
                )}
                <div>
                    {wheel.tyreName && (
                        <Text tag="div" variant="apheleia-body-small" color="grey-7">
                            <CarConfigLabel label="carConfigWheelTyreName" params={{ tyreName: wheel.tyreName }} />
                        </Text>
                    )}
                    {wheel.setPartNumber && (
                        <Text tag="div" variant="apheleia-body-small" color="grey-7">
                            <CarConfigLabel
                                label="carConfigWheelSetPartNumber"
                                params={{ setPartNumber: wheel.setPartNumber }}
                            />
                        </Text>
                    )}
                </div>

                <Text variant="apheleia-price">
                    {wheel.standard ? (
                        <CarConfigLabel label="carConfigIncluded" />
                    ) : wheel.price ? (
                        <Price
                            price={wheel.price}
                            forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                        />
                    ) : null}
                </Text>

                <div>
                    <figure className={styles["wheel-wrapper"]}>
                        <img
                            role="presentation"
                            {...getCarDBAssetSrcSet(
                                commonSettings,
                                [
                                    { width: 130, height: 130 },
                                    { width: 260, height: 260 },
                                    { width: 340, height: 340 },
                                    { width: 400, height: 400 },
                                    { width: 800, height: 800 },
                                ],
                                wheel.asset,
                            )}
                            width="130"
                            height="130"
                            sizes="(max-width: 1199px) 46vw, 18vw"
                            alt=""
                            loading="lazy"
                        />
                    </figure>
                    <div className={styles["wheel-footer"]}>
                        <div>
                            {!!wheel.tyre && (
                                <Button
                                    className={styles.center}
                                    variant="apheleia-text"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        trackWheelsTyreMoreInfoOpen();
                                        setShowInfo(true);
                                    }}
                                >
                                    <CarConfigLabel label="carConfigWheelMoreInfo" />
                                </Button>
                            )}
                        </div>
                        <Button
                            variant="btn-lbx"
                            sizing="full-width"
                            className={isSelectable ? styles.selectable : styles["wheel-button"]}
                            onClick={
                                isSelectable
                                    ? onClick
                                    : () => {
                                          trackViewAtmosphereWheels();
                                          scrollToTarget(CarConfigConfigureSectionsLbx.AtmosphereGrade);
                                      }
                            }
                        >
                            {isSelectable ? (
                                <span className={clsx(styles.check, isActive && styles["active-check"])}>
                                    <Icon variant="check" sizing="sm" />
                                </span>
                            ) : (
                                <CarConfigLabel label="carConfigLbxViewAtmosphere" />
                            )}
                        </Button>
                    </div>
                </div>
            </article>

            {!!wheel.tyre && (
                <SidePanel
                    open={showInfo}
                    title={sidePanelTitle}
                    onRequestClose={() => {
                        setShowInfo(false);
                    }}
                >
                    <WheelDetails tyres={[wheel.tyre]} />
                </SidePanel>
            )}
        </>
    );
};

export default memo(WinterWheel);
